import React, { useEffect, useState, createElement } from 'react';
import { ArrowRightIcon, CheckCircleIcon, MousePointerClickIcon, SearchIcon, LayersIcon, PuzzleIcon, SmartphoneIcon, ZapIcon, ShieldCheckIcon, ChevronDownIcon, ChevronUpIcon, WrenchIcon } from 'lucide-react';
import { FinalCtaSection } from '../components/FinalCtaSection';
export function WordPressWebsitesPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'WordPress websites | Flexibel & SEO-vriendelijk | Noord';
    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement;
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = 'WordPress website laten maken? Flexibele, snelle en SEO-vriendelijke websites gebouwd in WordPress. Zelf beheerbaar, veilig en uitbreidbaar. Vraag een offerte aan.';
    // Open Graph meta tags
    const ogTags: Record<string, string> = {
      'og:title': 'WordPress websites | Flexibel & SEO-vriendelijk | Noord',
      'og:description': 'WordPress website laten maken? Flexibele, snelle en SEO-vriendelijke websites gebouwd in WordPress.',
      'og:type': 'website',
      'og:url': 'https://noorddigital.nl/wordpress-websites',
      'og:site_name': 'Noord Digital',
      'og:locale': 'nl_NL'
    };
    Object.entries(ogTags).forEach(([property, content]) => {
      let tag = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.content = content;
    });
  }, []);
  const scrollToContact = (e: React.MouseEvent) => {
    e.preventDefault();
    document.getElementById('contact')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };
  const faqs = [{
    q: 'Is WordPress veilig voor mijn bedrijf?',
    a: 'Ja, WordPress is zeer veilig mits het goed wordt onderhouden. Wij zorgen voor de juiste beveiligingsplugins, sterke wachtwoorden en regelmatige updates om jouw wordpress website optimaal te beveiligen tegen hackers.'
  }, {
    q: 'Kan ik zelf content aanpassen?',
    a: "Absoluut! Dat is de grootste kracht van een WordPress website laten maken. Het systeem is erg gebruiksvriendelijk. Na oplevering krijg je van ons uitleg zodat je zelf eenvoudig teksten, afbeeldingen en nieuwe pagina's kunt toevoegen."
  }, {
    q: 'Hoe snel is een WordPress website?',
    a: "Een wordpress website kan razendsnel zijn, mits goed gebouwd. Wij gebruiken lichte thema's, optimaliseren afbeeldingen en gebruiken snelle hosting en caching om ervoor te zorgen dat jouw site direct laadt."
  }, {
    q: 'Is WordPress goed voor SEO?',
    a: 'WordPress is van nature zeer SEO-vriendelijk. Met de juiste inrichting en plugins (zoals Yoast of RankMath) heb je volledige controle over je vindbaarheid. Elk wordpress webdesign dat wij opleveren is technisch geoptimaliseerd voor Google.'
  }];
  return <main className="pt-16 lg:pt-[72px]" data-id="element-3140">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 bg-navy-900 overflow-hidden" data-id="element-3141">
        <div className="absolute inset-0 pointer-events-none" data-id="element-3142">
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl bg-accent" data-id="element-3143" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full opacity-10 blur-3xl bg-accent-light" data-id="element-3144" />
          <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '32px 32px'
        }} data-id="element-3145" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-3146">
          {/* Breadcrumb */}
          <nav className="flex items-center justify-center gap-2 text-sm mb-8 animate-fade-in-up" data-id="element-3147">
            <a href="#/" className="text-navy-400 hover:text-white transition-colors" data-id="element-3148">
              Home
            </a>
            <span className="text-navy-500" data-id="element-3149">/</span>
            <span className="text-accent-light font-medium" data-id="element-3150">
              WordPress websites
            </span>
          </nav>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6 animate-fade-in-up" data-id="element-3151">
            WordPress websites die groeien met je bedrijf
          </h1>
          <p className="text-lg lg:text-xl text-navy-200 max-w-2xl mx-auto mb-10 animate-fade-in-up-delay-1" data-id="element-3152">
            Flexibele, snelle en SEO-vriendelijke websites gebouwd in WordPress.
            De perfecte basis voor jouw online succes.
          </p>
          <div className="animate-fade-in-up-delay-2" data-id="element-3153">
            <a href="#contact" onClick={scrollToContact} className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-bold px-8 py-4 rounded-button shadow-medium hover:shadow-card transition-all duration-200 text-lg" data-id="element-3154">
              Plan een gesprek
              <ArrowRightIcon className="w-5 h-5" data-id="element-3155" />
            </a>
          </div>
        </div>
      </section>

      {/* Waarom WordPress Section */}
      <section className="py-24 bg-surface-muted" data-id="element-3156">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3157">
          <div className="text-center mb-16" data-id="element-3158">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-3159">
              Waarom een WordPress website laten maken?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-3160">
              Meer dan 40% van alle websites wereldwijd draait op WordPress. En
              dat is niet voor niets.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-3161">
            <div className="bg-white p-8 rounded-card shadow-soft hover:shadow-medium transition-shadow" data-id="element-3162">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-3163">
                <MousePointerClickIcon className="w-6 h-6 text-accent" data-id="element-3164" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-3165">
                Gebruiksvriendelijk
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-3166">
                Beheer zelf eenvoudig je teksten, afbeeldingen en blogartikelen
                zonder technische kennis.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft hover:shadow-medium transition-shadow" data-id="element-3167">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-3168">
                <SearchIcon className="w-6 h-6 text-accent" data-id="element-3169" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-3170">
                SEO vriendelijk
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-3171">
                De code structuur van WordPress wordt perfect begrepen door
                Google voor betere vindbaarheid.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft hover:shadow-medium transition-shadow" data-id="element-3172">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-3173">
                <LayersIcon className="w-6 h-6 text-accent" data-id="element-3174" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-3175">
                Flexibel uitbreidbaar
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-3176">
                Begin klein en breid later eenvoudig uit met een webshop,
                reserveringssysteem of leeromgeving.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft hover:shadow-medium transition-shadow" data-id="element-3177">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-3178">
                <PuzzleIcon className="w-6 h-6 text-accent" data-id="element-3179" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-3180">
                Veel integraties
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-3181">
                Koppel moeiteloos met je favoriete CRM, nieuwsbrief software of
                boekhoudpakket.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Wat je krijgt */}
      <section className="py-24 bg-white" data-id="element-3182">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3183">
          <div className="text-center mb-16" data-id="element-3184">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-3185">
              Standaard inbegrepen bij elk project
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-3186">
              Wanneer je kiest voor ons wordpress webdesign, krijg je altijd een
              compleet product.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-3187">
            <div className="border border-navy-100 p-8 rounded-card flex items-start gap-5" data-id="element-3188">
              <div className="w-12 h-12 rounded-xl bg-navy-50 flex items-center justify-center flex-shrink-0" data-id="element-3189">
                <SmartphoneIcon className="w-6 h-6 text-navy-700" data-id="element-3190" />
              </div>
              <div data-id="element-3191">
                <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-3192">
                  Responsive design
                </h3>
                <p className="text-slate-600" data-id="element-3193">
                  Jouw website werkt en oogt perfect op mobiel, tablet en
                  desktop. Cruciaal voor zowel gebruikerservaring als SEO.
                </p>
              </div>
            </div>
            <div className="border border-navy-100 p-8 rounded-card flex items-start gap-5" data-id="element-3194">
              <div className="w-12 h-12 rounded-xl bg-navy-50 flex items-center justify-center flex-shrink-0" data-id="element-3195">
                <SearchIcon className="w-6 h-6 text-navy-700" data-id="element-3196" />
              </div>
              <div data-id="element-3197">
                <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-3198">
                  SEO basis inrichting
                </h3>
                <p className="text-slate-600" data-id="element-3199">
                  We richten de technische SEO direct goed in en installeren
                  tools waarmee je zelf je vindbaarheid kunt optimaliseren.
                </p>
              </div>
            </div>
            <div className="border border-navy-100 p-8 rounded-card flex items-start gap-5" data-id="element-3200">
              <div className="w-12 h-12 rounded-xl bg-navy-50 flex items-center justify-center flex-shrink-0" data-id="element-3201">
                <ZapIcon className="w-6 h-6 text-navy-700" data-id="element-3202" />
              </div>
              <div data-id="element-3203">
                <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-3204">
                  Snelle laadtijd
                </h3>
                <p className="text-slate-600" data-id="element-3205">
                  Niemand houdt van wachten. We bouwen lichte websites en
                  gebruiken caching voor razendsnelle laadtijden.
                </p>
              </div>
            </div>
            <div className="border border-navy-100 p-8 rounded-card flex items-start gap-5" data-id="element-3206">
              <div className="w-12 h-12 rounded-xl bg-navy-50 flex items-center justify-center flex-shrink-0" data-id="element-3207">
                <ShieldCheckIcon className="w-6 h-6 text-navy-700" data-id="element-3208" />
              </div>
              <div data-id="element-3209">
                <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-3210">
                  Beveiliging
                </h3>
                <p className="text-slate-600" data-id="element-3211">
                  Standaard voorzien van SSL-certificaat, anti-spam maatregelen
                  en beveiligingsplugins om hackers buiten de deur te houden.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WordPress vs Andere systemen */}
      <section className="py-24 bg-navy-50" data-id="element-3212">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3213">
          <div className="text-center mb-16" data-id="element-3214">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-3215">
              WordPress vergeleken
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-3216">
              Waarom een wordpress website bedrijf vaak de beste keuze is.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12" data-id="element-3217">
            <div className="bg-white p-8 rounded-card shadow-card border-2 border-accent relative" data-id="element-3218">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-accent text-white text-xs font-bold px-4 py-1 rounded-full" data-id="element-3219">
                Onze keuze
              </div>
              <h3 className="text-2xl font-bold text-navy-900 mb-4 text-center" data-id="element-3220">
                WordPress
              </h3>
              <ul className="space-y-4" data-id="element-3221">
                <li className="flex items-start gap-3" data-id="element-3222">
                  <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0" data-id="element-3223" />
                  <span className="text-slate-700" data-id="element-3224">
                    100% eigendom van je website
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-3225">
                  <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0" data-id="element-3226" />
                  <span className="text-slate-700" data-id="element-3227">Eindeloos uitbreidbaar</span>
                </li>
                <li className="flex items-start gap-3" data-id="element-3228">
                  <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0" data-id="element-3229" />
                  <span className="text-slate-700" data-id="element-3230">
                    Beste voor SEO resultaten
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-3231">
                  <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0" data-id="element-3232" />
                  <span className="text-slate-700" data-id="element-3233">
                    Geen maandelijkse platformkosten
                  </span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-card shadow-soft" data-id="element-3234">
              <h3 className="text-2xl font-bold text-navy-900 mb-4 text-center" data-id="element-3235">
                Wix
              </h3>
              <ul className="space-y-4" data-id="element-3236">
                <li className="flex items-start gap-3" data-id="element-3237">
                  <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0" data-id="element-3238" />
                  <span className="text-slate-700" data-id="element-3239">
                    Makkelijk voor beginners
                  </span>
                </li>
                <li className="flex items-start gap-3 text-slate-400" data-id="element-3240">
                  <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-red-400 font-bold" data-id="element-3241">
                    ✕
                  </span>
                  <span data-id="element-3242">Je huurt de website (geen eigendom)</span>
                </li>
                <li className="flex items-start gap-3 text-slate-400" data-id="element-3243">
                  <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-red-400 font-bold" data-id="element-3244">
                    ✕
                  </span>
                  <span data-id="element-3245">Beperkte SEO mogelijkheden</span>
                </li>
                <li className="flex items-start gap-3 text-slate-400" data-id="element-3246">
                  <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-red-400 font-bold" data-id="element-3247">
                    ✕
                  </span>
                  <span data-id="element-3248">Moeilijk te verhuizen</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-card shadow-soft" data-id="element-3249">
              <h3 className="text-2xl font-bold text-navy-900 mb-4 text-center" data-id="element-3250">
                Squarespace
              </h3>
              <ul className="space-y-4" data-id="element-3251">
                <li className="flex items-start gap-3" data-id="element-3252">
                  <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0" data-id="element-3253" />
                  <span className="text-slate-700" data-id="element-3254">
                    Mooie standaard templates
                  </span>
                </li>
                <li className="flex items-start gap-3 text-slate-400" data-id="element-3255">
                  <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-red-400 font-bold" data-id="element-3256">
                    ✕
                  </span>
                  <span data-id="element-3257">Minder flexibel dan WordPress</span>
                </li>
                <li className="flex items-start gap-3 text-slate-400" data-id="element-3258">
                  <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-red-400 font-bold" data-id="element-3259">
                    ✕
                  </span>
                  <span data-id="element-3260">Hoge maandelijkse kosten</span>
                </li>
                <li className="flex items-start gap-3 text-slate-400" data-id="element-3261">
                  <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-red-400 font-bold" data-id="element-3262">
                    ✕
                  </span>
                  <span data-id="element-3263">Beperkte integraties NL markt</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="text-center" data-id="element-3264">
            <a href="#/kennisbank" className="inline-flex items-center gap-2 text-accent font-semibold hover:text-accent-hover transition-colors" data-id="element-3265">
              Lees ons uitgebreide artikel: WordPress vs Wix
              <ArrowRightIcon className="w-4 h-4" data-id="element-3266" />
            </a>
          </div>
        </div>
      </section>

      {/* WordPress Onderhoud */}
      <section className="py-24 bg-white" data-id="element-3267">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3268">
          <div className="bg-navy-900 rounded-3xl p-8 lg:p-12 text-center relative overflow-hidden" data-id="element-3269">
            <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '24px 24px'
          }} data-id="element-3270" />
            <WrenchIcon className="w-12 h-12 text-accent mx-auto mb-6 relative z-10" data-id="element-3271" />
            <h2 className="text-3xl font-bold text-white mb-4 relative z-10" data-id="element-3272">
              Zorgeloos WordPress Onderhoud
            </h2>
            <p className="text-navy-200 text-lg mb-8 max-w-2xl mx-auto relative z-10" data-id="element-3273">
              Een WordPress website heeft regelmatig updates nodig om veilig en
              snel te blijven. Wij nemen dit technische beheer graag voor je uit
              handen.
            </p>
            <a href="#/hosting-onderhoud" className="inline-flex items-center gap-2 bg-white text-navy-900 hover:bg-navy-50 font-bold px-8 py-4 rounded-button shadow-soft transition-all duration-200 relative z-10" data-id="element-3274">
              Bekijk onze onderhoudspakketten
              <ArrowRightIcon className="w-5 h-5" data-id="element-3275" />
            </a>
          </div>
        </div>
      </section>

      {/* Gerelateerde pagina's */}
      <section className="py-16 bg-surface-muted border-y border-navy-100" data-id="element-3276">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-3277">
          <h3 className="text-lg font-semibold text-navy-900 mb-6" data-id="element-3278">
            Ontdek meer:
          </h3>
          <div className="flex flex-wrap justify-center gap-4" data-id="element-3279">
            <a href="#/website-laten-maken" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-3280">
              Professionele website laten maken
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-3281">•</span>
            <a href="#/wordpress-vs-wix" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-3282">
              WordPress of Wix kiezen?
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-3283">•</span>
            <a href="#/webdesign-alkmaar" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-3284">
              Webdesign bureau Alkmaar
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-3285">•</span>
            <a href="#/hosting-onderhoud" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-3286">
              Website onderhoud uitbesteden
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-white" data-id="element-3287">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3288">
          <div className="text-center mb-16" data-id="element-3289">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-3290">
              Veelgestelde vragen over WordPress
            </h2>
          </div>

          <div className="space-y-4" data-id="element-3291">
            {faqs.map((faq, index) => <div key={index} className="bg-surface-muted rounded-xl shadow-sm border border-navy-50 overflow-hidden" data-id="element-3292">
                <button onClick={() => toggleFaq(index)} className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none" data-id="element-3293">
                  <span className="font-bold text-navy-900" data-id="element-3294">{faq.q}</span>
                  {openFaq === index ? <ChevronUpIcon className="w-5 h-5 text-accent flex-shrink-0" data-id="element-3295" /> : <ChevronDownIcon className="w-5 h-5 text-navy-400 flex-shrink-0" data-id="element-3296" />}
                </button>
                {openFaq === index && <div className="px-6 pb-5 text-slate-600 leading-relaxed" data-id="element-3297">
                    {faq.a}
                  </div>}
              </div>)}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <FinalCtaSection data-id="element-3298" />
    </main>;
}